import type { BlogPost, SocialMediaPost } from '@/lib/db/models';
import type { ContentKind } from '@/lib/social-media/content-kind';

export type SocialPostSource =
  | 'admin'
  | 'blog'
  | 'thought_leadership'
  | 'news'
  | 'press_release'
  | 'facebook'
  | 'instagram';

export type LinkedBlogSummary = {
  _id: string;
  title: string;
  slug: string;
  status: string;
  isDeleted: boolean;
  contentKind?: ContentKind;
};

export type EnrichedSocialPostRow = {
  _id: string;
  postId: string;
  platform?: 'facebook' | 'instagram';
  instagram_media_id?: string;
  message: string;
  links: string[];
  tags: string[];
  mediaUrl: string | null;
  postType: string;
  status: string;
  scheduledPublishAt: string | null;
  createdAt: string;
  updatedAt: string;
  postDate: string | null;
  likesCount: number;
  commentsCount: number;
  viewsCount: number;
  permalink: string;
  syncedFromFacebook: boolean;
  postSource: SocialPostSource;
  linkedBlog: LinkedBlogSummary | null;
  blogDeleted: boolean;
};

export function inferPostSource(
  doc: Pick<SocialMediaPost, 'postSource' | 'syncedFromFacebook' | 'rawApiResponse'>,
  hasBlogLink: boolean
): SocialPostSource {
  if (
    doc.postSource === 'admin' ||
    doc.postSource === 'blog' ||
    doc.postSource === 'thought_leadership' ||
    doc.postSource === 'news' ||
    doc.postSource === 'press_release' ||
    doc.postSource === 'facebook' ||
    doc.postSource === 'instagram'
  ) {
    return doc.postSource;
  }
  const raw = doc.rawApiResponse as { source?: string } | null | undefined;
  if (raw?.source === 'press_release_autopost') return 'press_release';
  if (raw?.source === 'news_autopost') return 'news';
  if (raw?.source === 'thought_leadership_autopost') return 'thought_leadership';
  if (raw?.source === 'instagram_sync' || raw?.source === 'instagram_admin_post') return 'instagram';
  if (raw?.source === 'blog_autopost' || hasBlogLink) return 'blog';
  if (doc.syncedFromFacebook) return 'facebook';
  return 'admin';
}

export function serializeSocialPostDoc(
  doc: SocialMediaPost & { _id: { toString: () => string } },
  linkedBlog: (Pick<BlogPost, 'title' | 'slug' | 'status' | 'isDeleted'> & {
    _id: { toString: () => string } | string;
    contentKind?: ContentKind;
  }) | null
): EnrichedSocialPostRow {
  const lb = linkedBlog
    ? {
        _id: String(linkedBlog._id),
        title: (linkedBlog.title || 'Untitled').trim(),
        slug: (linkedBlog.slug || '').trim(),
        status: linkedBlog.status || 'Draft',
        isDeleted: linkedBlog.isDeleted === true,
        ...(linkedBlog.contentKind ? { contentKind: linkedBlog.contentKind } : {}),
      }
    : null;

  const postSource = inferPostSource(doc, !!lb);

  return {
    _id: String(doc._id),
    postId: doc.postId || '',
    platform: doc.platform || 'facebook',
    instagram_media_id: doc.instagram_media_id || '',
    message: doc.message || '',
    links: doc.links || [],
    tags: doc.tags || [],
    mediaUrl: doc.mediaUrl ?? null,
    postType: doc.postType,
    status: doc.status,
    scheduledPublishAt: doc.scheduledPublishAt ? doc.scheduledPublishAt.toISOString() : null,
    createdAt: doc.createdAt?.toISOString?.() ?? new Date().toISOString(),
    updatedAt: doc.updatedAt?.toISOString?.() ?? new Date().toISOString(),
    postDate: doc.postDate?.toISOString?.() ?? doc.createdAt?.toISOString?.() ?? null,
    likesCount: doc.likesCount ?? 0,
    commentsCount: doc.commentsCount ?? 0,
    viewsCount: doc.viewsCount ?? 0,
    permalink: doc.permalink || '',
    syncedFromFacebook: !!doc.syncedFromFacebook,
    postSource,
    linkedBlog: lb,
    blogDeleted: !!lb?.isDeleted,
  };
}

export function postSourceLabel(source: SocialPostSource): string {
  switch (source) {
    case 'blog':
      return 'Blog';
    case 'thought_leadership':
      return 'Thought & Leadership';
    case 'news':
      return 'News';
    case 'press_release':
      return 'Press Release';
    case 'facebook':
      return 'Facebook';
    case 'instagram':
      return 'Instagram';
    default:
      return 'Admin';
  }
}
